import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import type { ShopifyCustomer, ShopifyOrder, ShopifyAddress } from "@/types/shopifyCustomer";
import {
  getStoredToken,
  getStoredRefreshToken,
  clearTokens,
  storeTokens,
  isTokenExpiringSoon,
  generatePKCEParams,
  storePKCEParams,
  buildAuthorizationUrl,
  getRedirectUri,
} from "@/lib/shopifyOAuth";
import { supabase } from "@/integrations/supabase/client";

interface ShopifyCustomerContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  customer: ShopifyCustomer | null;
  accessToken: string | null;
  initiateLogin: () => Promise<void>;
  exchangeCodeForToken: (code: string, codeVerifier: string) => Promise<boolean>;
  refreshAccessToken: () => Promise<boolean>;
  logout: () => void;
  fetchCustomer: () => Promise<ShopifyCustomer | null>;
  fetchOrders: (first?: number) => Promise<ShopifyOrder[]>;
  fetchOrder: (orderId: string) => Promise<ShopifyOrder | null>;
  fetchAddresses: () => Promise<{ addresses: ShopifyAddress[]; defaultAddressId: string | null }>;
  createAddress: (address: Record<string, string>) => Promise<boolean>;
  updateAddress: (addressId: string, address: Record<string, string>) => Promise<boolean>;
  deleteAddress: (addressId: string) => Promise<boolean>;
  setDefaultAddress: (addressId: string) => Promise<boolean>;
  updateCustomer: (input: Record<string, string>) => Promise<boolean>;
}

const ShopifyCustomerContext = createContext<ShopifyCustomerContextValue | null>(null);

// Actions that are part of the OAuth handshake itself and don't require auth.
const PUBLIC_ACTIONS = new Set(["getClientId", "exchangeToken", "refreshToken"]);

async function callEdge(action: string, params: Record<string, unknown> = {}) {
  const headers: Record<string, string> = {};
  if (!PUBLIC_ACTIONS.has(action)) {
    const idToken = localStorage.getItem("shopify_customer_id_token");
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
  }
  const { data, error } = await supabase.functions.invoke("shopify-oauth", {
    body: { action, ...params },
    headers,
  });
  if (error) throw error;
  return data;
}

function getIdTokenPayload(): Record<string, any> | null {
  const idToken = localStorage.getItem("shopify_customer_id_token");
  if (!idToken) return null;
  try {
    return JSON.parse(atob(idToken.split(".")[1]));
  } catch {
    return null;
  }
}

function getEmailFromIdToken(): string | null {
  return getIdTokenPayload()?.email || null;
}

function getSubFromIdToken(): string | null {
  return getIdTokenPayload()?.sub || null;
}

export function ShopifyCustomerProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState<ShopifyCustomer | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const hasHydrated = useRef(false);

  const isAuthenticated = !!accessToken;

  const logout = useCallback(() => {
    clearTokens();
    setAccessToken(null);
    setCustomer(null);
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const rt = getStoredRefreshToken();
    if (!rt) return false;
    try {
      const tokens = await callEdge("refreshToken", { refreshToken: rt });
      if (tokens.error) { logout(); return false; }
      storeTokens(tokens);
      setAccessToken(tokens.access_token);
      return true;
    } catch { logout(); return false; }
  }, [logout]);

  const fetchCustomer = useCallback(async (): Promise<ShopifyCustomer | null> => {
    const sub = getSubFromIdToken();
    const email = getEmailFromIdToken();
    const tokenPayload = getIdTokenPayload();
    if (!sub && !email) return null;
    try {
      let data;
      if (sub) {
        data = await callEdge("getCustomerById", { customerId: sub });
      } else {
        data = await callEdge("getCustomerByEmail", { email });
      }
      const c = data?.customer ?? null;
      // Use id_token claims as fallback for PII that may be redacted by Shopify
      const resolvedEmail = c?.email || tokenPayload?.email || null;
      const resolvedFirstName = c?.firstName || tokenPayload?.given_name || "";
      const resolvedLastName = c?.lastName || tokenPayload?.family_name || "";
      
      const customerData: ShopifyCustomer = {
        id: c?.id || sub || "",
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        emailAddress: resolvedEmail ? { emailAddress: resolvedEmail } : null,
        phoneNumber: c?.phone ? { phoneNumber: c.phone } : null,
        displayName: c?.displayName || `${resolvedFirstName} ${resolvedLastName}`.trim() || tokenPayload?.name || "",
        creationDate: c?.createdAt,
      };
      setCustomer(customerData);
      return c;
    } catch (e) {
      console.error("fetchCustomer failed:", e);
      return null;
    }
  }, []);

  const fetchOrders = useCallback(async (first = 20): Promise<ShopifyOrder[]> => {
    const sub = getSubFromIdToken();
    const email = getEmailFromIdToken();
    if (!sub && !email) return [];
    try {
      // Use customerId if available for more reliable lookup
      const data = await callEdge("getOrders", { email, customerId: sub, first });
      const edges = data?.data?.orders?.edges ?? [];
      return edges.map((e: any) => {
        const node = e.node;
        return {
          id: node.id,
          name: node.name,
          processedAt: node.processedAt,
          fulfillmentStatus: node.displayFulfillmentStatus,
          financialStatus: node.displayFinancialStatus,
          totalPrice: {
            amount: node.totalPriceSet?.shopMoney?.amount ?? "0",
            currencyCode: node.totalPriceSet?.shopMoney?.currencyCode ?? "USD",
          },
          lineItems: {
            edges: (node.lineItems?.edges ?? []).map((li: any) => ({
              node: {
                title: li.node.title,
                quantity: li.node.quantity,
                image: li.node.image,
                currentTotalPrice: {
                  amount: li.node.discountedTotalSet?.shopMoney?.amount ?? "0",
                  currencyCode: li.node.discountedTotalSet?.shopMoney?.currencyCode ?? "USD",
                },
              },
            })),
          },
        };
      });
    } catch (e) { console.error("fetchOrders failed:", e); return []; }
  }, []);

  const fetchOrder = useCallback(async (orderId: string): Promise<ShopifyOrder | null> => {
    try {
      const data = await callEdge("getOrder", { orderId });
      const node = data?.data?.order;
      if (!node) return null;
      return {
        id: node.id,
        name: node.name,
        processedAt: node.processedAt,
        fulfillmentStatus: node.displayFulfillmentStatus,
        financialStatus: node.displayFinancialStatus,
        totalPrice: { amount: node.totalPriceSet?.shopMoney?.amount ?? "0", currencyCode: node.totalPriceSet?.shopMoney?.currencyCode ?? "USD" },
        subtotal: node.subtotalPriceSet?.shopMoney,
        totalTax: node.totalTaxSet?.shopMoney,
        totalShipping: node.totalShippingPriceSet?.shopMoney,
        shippingAddress: node.shippingAddress,
        lineItems: {
          edges: (node.lineItems?.edges ?? []).map((li: any) => ({
            node: {
              title: li.node.title,
              quantity: li.node.quantity,
              image: li.node.image,
              currentTotalPrice: { amount: li.node.discountedTotalSet?.shopMoney?.amount ?? "0", currencyCode: li.node.discountedTotalSet?.shopMoney?.currencyCode ?? "USD" },
            },
          })),
        },
        fulfillments: node.fulfillments?.map((f: any) => ({
          status: f.status,
          createdAt: f.createdAt,
          trackingInformation: f.trackingInfo ?? [],
        })) ?? [],
      };
    } catch (e) { console.error("fetchOrder failed:", e); return null; }
  }, []);

  const fetchAddresses = useCallback(async () => {
    const sub = getSubFromIdToken();
    const email = getEmailFromIdToken();
    if (!sub && !email) return { addresses: [], defaultAddressId: null };
    try {
      const data = await callEdge("getAddresses", { email, customerId: sub });
      return {
        addresses: data?.addresses ?? [],
        defaultAddressId: data?.defaultAddressId ?? null,
      };
    } catch (e) {
      console.error("fetchAddresses failed:", e);
      return { addresses: [], defaultAddressId: null };
    }
  }, []);

  const createAddress = useCallback(async (address: Record<string, string>): Promise<boolean> => {
    if (!customer?.id) return false;
    try {
      const data = await callEdge("createAddress", { customerId: customer.id, address });
      return !!data?.success;
    } catch { return false; }
  }, [customer]);

  const updateAddress = useCallback(async (addressId: string, address: Record<string, string>): Promise<boolean> => {
    if (!customer?.id) return false;
    try {
      const data = await callEdge("updateAddress", { customerId: customer.id, addressId, address });
      return !!data?.success;
    } catch { return false; }
  }, [customer]);

  const deleteAddress = useCallback(async (addressId: string): Promise<boolean> => {
    if (!customer?.id) return false;
    try {
      const data = await callEdge("deleteAddress", { customerId: customer.id, addressId });
      return !!data?.success;
    } catch { return false; }
  }, [customer]);

  const setDefaultAddress = useCallback(async (addressId: string): Promise<boolean> => {
    if (!customer?.id) return false;
    try {
      const data = await callEdge("setDefaultAddress", { customerId: customer.id, addressId });
      return !!data?.success;
    } catch { return false; }
  }, [customer]);

  const updateCustomerFn = useCallback(async (input: Record<string, string>): Promise<boolean> => {
    if (!customer?.id) return false;
    try {
      const data = await callEdge("updateCustomer", { customerId: customer.id, input });
      const ok = !data?.data?.customerUpdate?.userErrors?.length;
      if (ok) await fetchCustomer();
      return ok;
    } catch { return false; }
  }, [customer, fetchCustomer]);

  const initiateLogin = useCallback(async () => {
    const clientData = await callEdge("getClientId");
    const pkce = await generatePKCEParams();
    storePKCEParams(pkce);
    const url = buildAuthorizationUrl(clientData.clientId, pkce.codeChallenge, pkce.state);
    window.location.href = url;
  }, []);

  const exchangeCodeForToken = useCallback(async (code: string, codeVerifier: string): Promise<boolean> => {
    try {
      const tokens = await callEdge("exchangeToken", {
        code,
        codeVerifier,
        redirectUri: getRedirectUri(),
      });
      if (tokens.error) return false;
      storeTokens(tokens);
      setAccessToken(tokens.access_token);
      return true;
    } catch { return false; }
  }, []);

  // Hydrate on mount
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    const token = getStoredToken();
    if (token) {
      setAccessToken(token);
      if (isTokenExpiringSoon()) {
        refreshAccessToken().finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [refreshAccessToken]);

  // Fetch customer when token available
  useEffect(() => {
    if (accessToken && !customer) {
      fetchCustomer();
    }
  }, [accessToken, customer, fetchCustomer]);

  return (
    <ShopifyCustomerContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        customer,
        accessToken,
        initiateLogin,
        exchangeCodeForToken,
        refreshAccessToken,
        logout,
        fetchCustomer,
        fetchOrders,
        fetchOrder,
        fetchAddresses,
        createAddress,
        updateAddress,
        deleteAddress,
        setDefaultAddress,
        updateCustomer: updateCustomerFn,
      }}
    >
      {children}
    </ShopifyCustomerContext.Provider>
  );
}

export function useShopifyCustomer() {
  const ctx = useContext(ShopifyCustomerContext);
  if (!ctx) throw new Error("useShopifyCustomer must be used within ShopifyCustomerProvider");
  return ctx;
}
